import React, { type ReactElement, Children, Fragment, cloneElement, useMemo, useCallback, useState, type JSX } from "react";
import { twMerge } from "tailwind-merge";

import SupChevronRight from "../../../icons/ChevronRight";
import SupDotsHorizontalRounded from "../../../icons/DotsHorizontalRounded";

import { type BreadcrumbProps, SupBreadcrumb } from "./components/Breadcrumb";

interface BreadcrumbsProps<P extends object> {
  className?: string;
  icon?: JSX.Element;
  maxItems?: number;
  itemsAfterCollapse?: number;
  itemsBeforeCollapse?: number;
  separator?: string | JSX.Element;
  children?: ReactElement<BreadcrumbProps<P>, typeof SupBreadcrumb> | ReactElement<BreadcrumbProps<P>, typeof SupBreadcrumb>[];
}

export function SupBreadcrumbs<P extends object>({
  className,
  icon,
  separator = <SupChevronRight />,
  maxItems = 8,
  itemsAfterCollapse = 1,
  itemsBeforeCollapse = 1,
  children = [],
}: BreadcrumbsProps<P>) {
  const [collapsed, setCollapsed] = useState(true);
  const isCollapsed = Array.isArray(children) && children.length > maxItems && collapsed;

  const separatorEl = useMemo(() => <span className="sup-breadcrumb-separator inline-flex w-10 justify-center">{separator}</span>, [separator]);

  const [beforeCollapse, afterCollapse] = useMemo(() => {
    const max = maxItems - 1;
    const beforeCollapse = itemsBeforeCollapse < max ? itemsBeforeCollapse : max - 1;
    const afterCollapse = itemsAfterCollapse < max - beforeCollapse ? itemsAfterCollapse : max - beforeCollapse;

    return [beforeCollapse, afterCollapse];
  }, [maxItems, itemsAfterCollapse, itemsBeforeCollapse]);

  const renderChildren = useCallback(({ children = [], icon, separator: separatorEl }: BreadcrumbsProps<P>) => {
    return Children.map(children, (breadcrumb, i) => (
      <Fragment key={`breadcrumb-${i}`}>
        {i !== 0 && separatorEl}
        {cloneElement(breadcrumb, { icon })}
      </Fragment>
    ));
  }, []);

  const renderCollapsedChildren = useCallback(
    ({ children, icon, separator: separatorEl, itemsBeforeCollapse: beforeCollapse = 1, itemsAfterCollapse: afterCollapse = 1 }: BreadcrumbsProps<P>) => {
      if (!Array.isArray(children)) {
        return children;
      }

      return (
        <Fragment>
          {children.slice(0, beforeCollapse).map((breadcrumb, i) => (
            <Fragment key={`breadcrumb-before-${i}`}>
              {cloneElement(breadcrumb, { icon })}
              {separatorEl}
            </Fragment>
          ))}

          <button
            onClick={() => {
              setCollapsed(false);
            }}
          >
            <SupDotsHorizontalRounded />
          </button>

          {children.slice(-afterCollapse).map((breadcrumb, i) => (
            <Fragment key={`breadcrumb-after-${i}`}>
              {separatorEl}
              {cloneElement(breadcrumb, { icon: breadcrumb.props.icon ?? icon })}
            </Fragment>
          ))}
        </Fragment>
      );
    },
    []
  );

  return (
    <div className={twMerge("sup-breadcrumbs flex gap-2 items-center", className)}>
      {isCollapsed
        ? renderCollapsedChildren({ children, icon, separator: separatorEl, itemsBeforeCollapse: beforeCollapse, itemsAfterCollapse: afterCollapse })
        : renderChildren({ children, icon, separator: separatorEl })}
    </div>
  );
}

export * from "./components/Breadcrumb";
